// components/ui/ListToolbar.tsx — the one toolbar every list wears: the search box on the left; on the right a
// spinner while it refetches, "Export CSV" (shown with `<page>.export`) and the Filter button, always right-most.
// DataTable draws it inside its card; a list that is not a DataTable (cards, a grid, a plan) draws it on its own.
import type { ReactNode } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Input } from './index';
import { ExportCsvButton, type CsvExport } from './ExportCsvButton';

export function ListToolbar({ search, onSearch, searchPlaceholder = 'Search…', fetching, exportCsv, filters, standalone, className = '' }: {
  search?: string;
  onSearch?: (s: string) => void;
  searchPlaceholder?: string;
  /** A small spinner while the list refetches. */
  fetching?: boolean;
  /** Export exactly what is shown (the caller merges its search and sort into the query). */
  exportCsv?: CsvExport;
  /** The Filter button (FilterMenu / ListFilter), and a Columns button before it where a table has no Filter card. */
  filters?: ReactNode;
  /** Drawn as its own card above a list that is not inside one (cards, a grid); default: the top row of a card. */
  standalone?: boolean;
  className?: string;
}) {
  return (
    <div className={`flex flex-wrap items-center gap-2 min-h-[48px] ${standalone ? 'surface px-3 py-2' : 'border-b border-ink-200 px-3 py-2'} ${className}`}>
      {onSearch && (
        <div className="relative min-w-0 flex-1 sm:flex-none">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-ink-400" />
          <Input value={search ?? ''} onChange={(e) => onSearch(e.target.value)} placeholder={searchPlaceholder} aria-label={searchPlaceholder} className="w-full pl-8 sm:w-60" />
        </div>
      )}
      <div className="ml-auto flex max-w-full flex-wrap items-center justify-end gap-2">
        {fetching && <Loader2 className="h-3.5 w-3.5 animate-spin text-ink-400" />}
        {exportCsv && <ExportCsvButton {...exportCsv} />}
        {filters}
      </div>
    </div>
  );
}
